import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import { Music, Loader as Loader2, ChevronRight, Trophy, Play, Pause, Sparkles, Video as VideoIcon, Headphones, X, Share2, LogOut } from 'lucide-react';
import { KioskService, KioskSongEntry, getStoredKioskSession, clearKioskSession, KioskLeagueBranding, resolveLyricsPublicUrl } from '../services/KioskService';
import KioskShell from '../components/KioskShell';
import { VocalBlueprintModal } from '../components/VocalBlueprintModal';
import { LyricsDisplay } from '../components/LyricsDisplay';
import { useKioskBasePath } from '../hooks/useKioskBasePath';

export const KioskMe: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tmQuery = searchParams.get('testmode') === 'true' ? '?testmode=true' : '';
  const basePath = useKioskBasePath();
  const [league, setLeague] = useState<KioskLeagueBranding | null>(null);
  const [me, setMe] = useState<Record<string, any> | null>(null);
  const [songs, setSongs] = useState<KioskSongEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [analysis, setAnalysis] = useState<Record<string, any> | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);

  const [practiceSong, setPracticeSong] = useState<KioskSongEntry | null>(null);
  const [practiceLyrics, setPracticeLyrics] = useState<string | null>(null);
  const [practiceTime, setPracticeTime] = useState(0);
  const [practicePlaying, setPracticePlaying] = useState(false);
  const practiceAudioRef = useRef<HTMLAudioElement | null>(null);
  const practiceAnimRef = useRef<number | null>(null);

  const openPractice = useCallback(async (song: KioskSongEntry) => {
    setPracticeSong(song);
    setPracticeLyrics(null);
    setPracticeTime(0);
    setPracticePlaying(false);

    if (song.lyrics_url) {
      const url = resolveLyricsPublicUrl(song.lyrics_url);
      if (url) {
        try {
          const res = await fetch(url);
          if (res.ok) setPracticeLyrics(await res.text());
        } catch {}
      }
    }
  }, []);

  const closePractice = useCallback(() => {
    if (practiceAudioRef.current) {
      practiceAudioRef.current.pause();
      practiceAudioRef.current.src = '';
    }
    if (practiceAnimRef.current) cancelAnimationFrame(practiceAnimRef.current);
    setPracticeSong(null);
    setPracticeLyrics(null);
    setPracticeTime(0);
    setPracticePlaying(false);
  }, []);

  const togglePracticePlayback = useCallback(() => {
    const audio = practiceAudioRef.current;
    if (!audio) return;
    if (practicePlaying) {
      audio.pause();
      if (practiceAnimRef.current) cancelAnimationFrame(practiceAnimRef.current);
      setPracticePlaying(false);
    } else {
      audio.play();
      setPracticePlaying(true);
      const tick = () => {
        setPracticeTime(audio.currentTime);
        practiceAnimRef.current = requestAnimationFrame(tick);
      };
      practiceAnimRef.current = requestAnimationFrame(tick);
    }
  }, [practicePlaying]);

  useEffect(() => {
    return () => {
      if (practiceAnimRef.current) cancelAnimationFrame(practiceAnimRef.current);
    };
  }, []);

  useEffect(() => {
    if (!slug) return;
    const session = getStoredKioskSession();
    if (!session || session.leagueSlug !== slug) {
      navigate(`/${basePath}/${slug}${tmQuery}`);
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const [lb, meData, songData] = await Promise.all([
          KioskService.getLeaderboard(slug),
          KioskService.getMe(),
          KioskService.getLeagueSongs(slug),
        ]);
        setLeague(lb.league);
        setMe(meData as Record<string, any>);
        setSongs(songData);
        const mr = meData as Record<string, any> | null;
        const latest = mr?.submissions?.[0];
        if (latest?.id) {
          try {
            const a = await KioskService.getSubmissionAnalysis(latest.id);
            setAnalysis(a);
          } catch { /* ignore */ }
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, navigate]);

  const handlePlaybackToggle = async (allow: boolean) => {
    try {
      await KioskService.updatePlaybackPreference(allow);
      const updated = await KioskService.getMe();
      setMe(updated as Record<string, any>);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    }
  };

  const signOut = () => {
    clearKioskSession();
    navigate(`/${basePath}/${slug}${tmQuery}`);
  };

  const openAnalysis = async () => {
    if (!latestSubmission?.id) return;
    setAnalysisOpen(true);
    if (analysis && analysis.submission_id === latestSubmission.id) return;
    setAnalysisLoading(true);
    try {
      const data = await KioskService.getSubmissionAnalysis(latestSubmission.id);
      setAnalysis(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load analysis');
      setAnalysisOpen(false);
    } finally {
      setAnalysisLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white/80">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }
  if (!league || !me) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white/80">
        {error || 'Not found'}
      </div>
    );
  }

  const participant = me.participant;
  const submissions = (me.submissions || []) as Array<Record<string, any>>;
  const latestSubmission = submissions[0];
  const hasSubmitted = submissions.length > 0;
  const bestScore = me.best_score;
  const accent = league.accent_color || '#f97316';
  const primary = league.primary_color || '#f97316';

  const getStatusDisplay = () => {
    if (participant.status === 'scored' || (hasSubmitted && bestScore != null)) {
      return { label: 'Complete', color: '#22c55e' };
    }
    if (hasSubmitted && bestScore == null) {
      const submittedAt = new Date(latestSubmission.submitted_at).getTime();
      const minutesSince = (Date.now() - submittedAt) / 60000;
      if (minutesSince > 3) {
        return { label: 'Scoring delayed', color: '#f59e0b' };
      }
      return { label: 'Processing...', color: '#f59e0b' };
    }
    if (participant.status === 'queued') {
      const joinedAt = new Date(participant.joined_at).getTime();
      const minutesSince = (Date.now() - joinedAt) / 60000;
      if (minutesSince > 2) {
        return { label: 'Not submitted', color: '#ef4444' };
      }
      return { label: 'Ready to sing', color: '#94a3b8' };
    }
    return { label: participant.status, color: '#94a3b8' };
  };
  const statusDisplay = getStatusDisplay();

  const songLookup = new Map(songs.map((s) => [s.id, s]));
  const submittedSong = latestSubmission ? songLookup.get(latestSubmission.song_id) : null;
  const isVideoTake = analysis?.recording_type === 'video' || latestSubmission?.recording_type === 'video';
  const videoMuxStatus = latestSubmission?.video_mux_status;
  const isVideoRendering = isVideoTake && videoMuxStatus && videoMuxStatus !== 'completed';
  const playbackUrl = latestSubmission?.mixed_audio_url || latestSubmission?.recording_url;
  const shareUrl = latestSubmission?.id ? `${window.location.origin}/${basePath}/${slug}/share/${latestSubmission.id}` : null;

  return (
    <KioskShell
      league={league}
      displayName={participant.display_name}
      leagueSlug={slug!}
      showSignOut
      onSignOut={signOut}
    >
      {error && (
        <div className="mb-4 p-3 bg-red-500/15 border border-red-400/30 rounded-lg text-red-200 text-sm">
          {error}
        </div>
      )}

      <div className="bg-white/5 backdrop-blur-md border border-white/15 rounded-2xl p-6 mb-6">
        <div className="grid grid-cols-2 gap-4 text-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-white/50 mb-1">Your Score</div>
            <div className="text-4xl font-black" style={{ color: accent }}>
              {bestScore != null ? Number(bestScore).toFixed(1) : '-'}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-white/50 mb-1">Status</div>
            <div className="text-xl font-bold" style={{ color: statusDisplay.color }}>
              {statusDisplay.label}
            </div>
          </div>
        </div>

        {statusDisplay.label === 'Not submitted' && (
          <div className="mt-4 p-3 bg-red-500/10 border border-red-400/20 rounded-lg text-sm text-red-200">
            Your recording did not upload successfully. Please select a song below and try again.
          </div>
        )}
        {statusDisplay.label === 'Scoring delayed' && (
          <div className="mt-4 p-3 bg-amber-500/10 border border-amber-400/20 rounded-lg text-sm text-amber-200">
            Scoring is taking longer than expected. Your take was received — check back in a few minutes.
          </div>
        )}
        {statusDisplay.label === 'Processing...' && (
          <div className="mt-4 p-3 bg-amber-500/10 border border-amber-400/20 rounded-lg text-sm text-amber-200 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Your take is being scored. This usually takes under a minute.
          </div>
        )}
      </div>

      {hasSubmitted ? (
        <div className="bg-white/5 backdrop-blur-md border border-white/15 rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-5 h-5" style={{ color: accent }} />
            <h2 className="text-lg font-bold">Your Take</h2>
          </div>

          {submittedSong && (
            <div className="flex items-center gap-4 p-4 bg-black/30 rounded-xl mb-4">
              {submittedSong.cover_url ? (
                <img
                  src={submittedSong.cover_url}
                  alt={submittedSong.title}
                  className="w-16 h-16 rounded-lg object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-white/10 flex items-center justify-center">
                  <Music className="w-6 h-6 text-white/50" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{submittedSong.title}</div>
                <div className="text-sm text-white/60 truncate">{submittedSong.artist}</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-black" style={{ color: accent }}>
                  {Number(latestSubmission.score ?? 0).toFixed(1)}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-white/50">Points</div>
              </div>
            </div>
          )}

          {isVideoRendering && (
            <div className="flex items-center gap-2 p-3 bg-blue-500/10 border border-blue-400/20 rounded-lg text-sm text-blue-200 mb-4">
              <Loader2 className="w-4 h-4 animate-spin" />
              Rendering Video — your final video is being processed. Check back shortly.
            </div>
          )}

          {playbackUrl && (
            <div className="flex flex-wrap items-center gap-3">
              {shareUrl && latestSubmission?.mixed_audio_url ? (
                <a
                  href={shareUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-white hover:brightness-110 transition shadow-lg"
                  style={{ backgroundColor: primary }}
                >
                  {isVideoTake ? <VideoIcon className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  {isVideoTake ? 'Watch your take' : 'Listen to your take'}
                </a>
              ) : (
                <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-white/50 bg-white/5 border border-white/10">
                  <Headphones className="w-4 h-4" />
                  Mix processing...
                </div>
              )}
              <button
                onClick={openAnalysis}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-white bg-white/10 hover:bg-white/20 border border-white/20 transition"
              >
                <Sparkles className="w-4 h-4" />
                View Analysis
              </button>
              {shareUrl && (
                <a
                  href={shareUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-white bg-white/10 hover:bg-white/20 border border-white/20 transition"
                >
                  <Share2 className="w-4 h-4" />
                  Share
                </a>
              )}
            </div>
          )}

          <div className="mt-5 p-3 bg-black/25 border border-white/10 rounded-lg text-xs text-white/70 leading-relaxed">
            You've already submitted your one take for this league. Your score is locked in.
          </div>
        </div>
      ) : (
        <section className="bg-white/5 backdrop-blur-md border border-white/15 rounded-2xl p-6 mb-6">
          <h2 className="text-lg font-bold mb-2 flex items-center gap-2">
            <Music className="w-5 h-5" style={{ color: accent }} /> Pick your song
          </h2>
          <p className="text-sm text-white/60 mb-4">
            You get <span className="font-semibold text-white">one take</span>. Choose wisely.
          </p>
          {songs.length === 0 ? (
            <p className="text-white/60 text-sm">The host hasn't added any songs yet. Check back soon.</p>
          ) : (
            <ul className="space-y-1">
              {songs.map((s) => (
                <li key={s.id} className="flex items-center gap-2 py-2 px-3 rounded-xl hover:bg-white/10 transition border border-transparent hover:border-white/15">
                  {s.cover_url ? (
                    <img src={s.cover_url} alt={s.title} className="w-12 h-12 rounded-lg object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center">
                      <Music className="w-5 h-5 text-white/50" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{s.title}</div>
                    <div className="text-sm text-white/60 truncate">{s.artist}</div>
                  </div>
                  {s.full_mix_url && (
                    <button
                      onClick={() => openPractice(s)}
                      className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-white/10 hover:bg-white/20 border border-white/15 text-white/80 hover:text-white transition"
                      title="Practice - listen to the full song with lyrics"
                    >
                      <Headphones className="w-4 h-4" />
                      <span className="hidden sm:inline">Practice</span>
                    </button>
                  )}
                  <Link
                    to={`/${basePath}/${slug}/sing/${s.id}${tmQuery}`}
                    className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-white transition"
                    style={{ backgroundColor: accent }}
                  >
                    <Music className="w-4 h-4" />
                    Sing
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <Link
          to={`/${basePath}/${slug}/leaderboard${tmQuery}`}
          className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-white hover:brightness-110 transition shadow-lg"
          style={{ backgroundColor: primary }}
        >
          <Trophy className="w-5 h-5" /> View Leaderboard
        </Link>
        <button
          onClick={signOut}
          className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-white/90 bg-white/10 hover:bg-white/20 border border-white/20 transition"
        >
          <LogOut className="w-5 h-5" /> Sign Out
        </button>
      </div>

      {basePath !== 'sing-along' && (
        <section className="bg-white/5 backdrop-blur-md border border-white/15 rounded-2xl p-6">
          <h2 className="text-sm font-bold mb-2 uppercase tracking-wider text-white/80">Privacy</h2>
          <label className="flex items-start gap-3 text-sm text-white/80">
            <input
              type="checkbox"
              checked={participant.allow_playback}
              onChange={(e) => handlePlaybackToggle(e.target.checked)}
              className="mt-1 w-4 h-4"
              style={{ accentColor: accent }}
            />
            <span>
              Allow other participants to listen to my recording on the leaderboard.
              Uncheck to keep your take private.
            </span>
          </label>
        </section>
      )}
      <VocalBlueprintModal
        isOpen={analysisOpen}
        onClose={() => setAnalysisOpen(false)}
        compressedData={analysisLoading ? null : (analysis?.report_compressed_b64 || null)}
        title={submittedSong ? `${submittedSong.title} - Analysis` : 'Your Vocal Blueprint'}
        lyricsUrl={analysis?.lyrics_url}
        vocalRecordingUrl={latestSubmission?.recording_url}
        leadVocalUrl={analysis?.lead_vocal_url}
        finalRecordingUrl={isVideoTake ? latestSubmission?.recording_url : undefined}
        coachingResponse={analysis?.coaching_response ?? null}
        overallScore={analysis?.overall_score ?? latestSubmission?.score ?? null}
        pitchAccuracy={analysis?.pa_pitch_accuracy ?? analysis?.pitch_accuracy ?? null}
        timingAccuracy={analysis?.pa_timing_accuracy ?? analysis?.timing_accuracy ?? null}
      />

      {practiceSong && (
        <div className="fixed inset-0 z-[70] flex flex-col bg-black/95 backdrop-blur-md">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="flex items-center gap-3 min-w-0">
              {practiceSong.cover_url ? (
                <img src={practiceSong.cover_url} alt={practiceSong.title} className="w-10 h-10 rounded-lg object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                  <Music className="w-4 h-4 text-white/50" />
                </div>
              )}
              <div className="min-w-0">
                <div className="font-semibold text-white truncate">{practiceSong.title}</div>
                <div className="text-xs text-white/60 truncate">{practiceSong.artist}</div>
              </div>
            </div>
            <button
              onClick={closePractice}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 relative overflow-hidden">
            {practiceLyrics ? (
              <LyricsDisplay lrcContent={practiceLyrics} currentTime={practiceTime} />
            ) : (
              <div className="flex items-center justify-center h-full text-white/50 text-lg">
                No lyrics available for this song
              </div>
            )}
          </div>

          <div className="px-4 py-4 border-t border-white/10 flex items-center justify-center gap-4">
            <button
              onClick={togglePracticePlayback}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white text-sm transition shadow-lg"
              style={{ backgroundColor: accent }}
            >
              {practicePlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              {practicePlaying ? 'Pause' : 'Play'}
            </button>
          </div>

          <audio
            ref={practiceAudioRef}
            src={practiceSong.full_mix_url || practiceSong.audio_url || ''}
            onEnded={() => {
              setPracticePlaying(false);
              if (practiceAnimRef.current) cancelAnimationFrame(practiceAnimRef.current);
            }}
            className="hidden"
          />
        </div>
      )}
    </KioskShell>
  );
};

export default KioskMe;
